import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Check, X, Search, ArrowUpDown, ArrowUp, ArrowDown, Eye, ChevronDown, ChevronRight, Trash2, Hand, Package } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Ofertas = () => {
  const { user } = useAuth();
  const [ofertas, setOfertas] = useState([]);
  const [publicaciones, setPublicaciones] = useState({});
  const [compradores, setCompradores] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedPub, setExpandedPub] = useState(null);
  const [processingOffer, setProcessingOffer] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentTab, setCurrentTab] = useState('nuevas');
  const [sortField, setSortField] = useState('hora');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    const delay = setTimeout(() => { setSearchTerm(searchInput); }, 600);
    return () => clearTimeout(delay);
  }, [searchInput]);

  useEffect(() => { loadOfertas(); }, [searchTerm, currentTab, sortField, sortDirection]);

  const loadOfertas = async () => {
    setLoading(true);
    try {
      const estadoFilter = currentTab === 'nuevas' ? 'Pendiente' : currentTab === 'aceptadas' ? 'Aceptada' : 'Rechazada';
      const res = await axios.get(`${API}/ofertas/paginated`, {
        params: { skip: 0, limit: 100, searchTerm: searchTerm || undefined, estadoFilter, sortField, sortDirection }
      });
      setOfertas(res.data.items || []);
      setTotalCount(res.data.total || 0);

      // Build maps
      const pubMap = {};
      (res.data.publicaciones || []).forEach(p => { pubMap[p.id] = p; });
      setPublicaciones(pubMap);
      const compMap = {};
      (res.data.compradores || []).forEach(c => { compMap[c.id] = c; });
      setCompradores(compMap);
    } catch {
      toast.error('Error al cargar bids');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (ofertaId) => {
    if (processingOffer) return;
    setProcessingOffer(ofertaId);
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado: 'Aceptada' });
      toast.success('Bid aceptado');
      loadOfertas();
    } catch { toast.error('Error al aceptar bid'); }
    finally { setProcessingOffer(null); }
  };

  const handleReject = async (ofertaId) => {
    if (processingOffer) return;
    setProcessingOffer(ofertaId);
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado: 'Rechazada' });
      toast.success('Bid rechazado');
      loadOfertas();
    } catch { toast.error('Error al rechazar bid'); }
    finally { setProcessingOffer(null); }
  };

  const handleDelete = async (ofertaId) => {
    if (!window.confirm('¿Eliminar este bid?')) return;
    try {
      await axios.delete(`${API}/ofertas/${ofertaId}`);
      toast.success('Bid eliminado');
      loadOfertas();
    } catch { toast.error('Error al eliminar bid'); }
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('desc'); }
  };

  const SortableHeader = ({ field, children }) => (
    <th className="text-left p-4 font-medium cursor-pointer hover:bg-accent/50" onClick={() => handleSort(field)}>
      <div className="flex items-center gap-2">
        {children}
        {sortField === field ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />) : <ArrowUpDown className="h-4 w-4 opacity-30" />}
      </div>
    </th>
  );

  // Group by publicacion
  const grouped = {};
  ofertas.forEach(o => {
    if (!grouped[o.publicacionId]) grouped[o.publicacionId] = [];
    grouped[o.publicacionId].push(o);
  });
  const pubIds = Object.keys(grouped);

  return (
    <div className="space-y-6 bg-background min-h-screen">
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-semibold tracking-tight">Bids</h1>
          <p className="text-muted-foreground mt-1">{totalCount} bids encontrados</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Card className="p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Buscar por frigorífico, comprador..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="pl-10" />
          </div>
        </Card>

        <Tabs value={currentTab} onValueChange={setCurrentTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="nuevas">Nuevas {currentTab === 'nuevas' ? `(${totalCount})` : ''}</TabsTrigger>
            <TabsTrigger value="aceptadas">Aceptadas {currentTab === 'aceptadas' ? `(${totalCount})` : ''}</TabsTrigger>
            <TabsTrigger value="rechazadas">Rechazadas {currentTab === 'rechazadas' ? `(${totalCount})` : ''}</TabsTrigger>
          </TabsList>

          <TabsContent value={currentTab} className="space-y-4">
            {loading && ofertas.length === 0 ? (
              <Card className="p-12 text-center"><p className="text-muted-foreground">Cargando bids...</p></Card>
            ) : totalCount === 0 ? (
              <Card className="p-12 text-center"><p className="text-muted-foreground">No hay bids</p></Card>
            ) : (
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[700px]">
                    <thead className="border-b">
                      <tr>
                        <th className="p-4 w-10"></th>
                        <SortableHeader field="publicacion">Oferta</SortableHeader>
                        <SortableHeader field="monto">Monto</SortableHeader>
                        <th className="text-left p-4 font-medium">Bids</th>
                        <th className="text-right p-4 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pubIds.map(pubId => {
                        const pub = publicaciones[pubId];
                        const pubOfertas = grouped[pubId];
                        const isExpanded = expandedPub === pubId;
                        return (
                          <React.Fragment key={pubId}>
                            <tr className="border-b hover:bg-accent/50 cursor-pointer" onClick={() => setExpandedPub(isExpanded ? null : pubId)}>
                              <td className="p-4">
                                {isExpanded ? <ChevronDown className="h-5 w-5 text-muted-foreground" /> : <ChevronRight className="h-5 w-5 text-muted-foreground" />}
                              </td>
                              <td className="p-4">
                                <div className="font-medium">{pub?.frigorificoNombre || 'N/A'}</div>
                                <div className="text-sm text-muted-foreground flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs">{pub?.tipo}</Badge>
                                  #{pub?.plantaNumero}
                                  <span className="flex items-center gap-1"><Package className="h-3 w-3" />{pub?.fcls?.length || 0} FCLs</span>
                                </div>
                              </td>
                              <td className="p-4">
                                <div className="font-medium">
                                  {pub?.fcls ? `USD ${pub.fcls.reduce((s, f) => s + (f.precio || 0), 0).toLocaleString()}` : '—'}
                                </div>
                                <div className="text-xs text-muted-foreground">total FCLs</div>
                              </td>
                              <td className="p-4"><Badge variant="secondary">{pubOfertas.length}</Badge></td>
                              <td className="p-4">
                                <div className="flex justify-end">
                                  {pub && (
                                    <Button size="sm" variant="ghost" asChild onClick={(e) => e.stopPropagation()}>
                                      <Link to={`/publicaciones/${pub.id}`}><Eye className="h-4 w-4" /></Link>
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {isExpanded && pubOfertas.map((oferta, idx) => {
                              const comp = compradores[oferta.compradorId];
                              return (
                                <tr key={oferta.id} className="border-b bg-accent/20 animate-in fade-in" style={{ animationDelay: `${idx * 50}ms` }}>
                                  <td className="p-4"></td>
                                  <td className="p-4 pl-8">
                                    <div className="font-medium">{comp?.nombre || 'N/A'}</div>
                                    {comp?.empresa && <div className="text-sm text-muted-foreground">{comp.empresa}</div>}
                                  </td>
                                  <td className="p-4">
                                    <div className="text-lg font-bold text-primary">
                                      USD {oferta.monto?.toLocaleString()}
                                      {oferta.esManual && (
                                        <Badge variant="outline" className="ml-2 gap-1 text-[11px] h-5 border-primary text-primary">
                                          <Hand className="h-3 w-3" /> Manual
                                        </Badge>
                                      )}
                                    </div>
                                  </td>
                                  <td className="p-4 text-sm text-muted-foreground">
                                    {format(new Date(oferta.hora), 'dd/MM/yyyy HH:mm')}
                                  </td>
                                  <td className="p-4">
                                    <div className="flex justify-end gap-2">
                                      {user?.rol === 'SuperAdmin' && (
                                        <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={(e) => { e.stopPropagation(); handleDelete(oferta.id); }}>
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      )}
                                      {oferta.estado === 'Pendiente' && (
                                        <>
                                          <Button size="sm" disabled={!!processingOffer} onClick={(e) => { e.stopPropagation(); handleAccept(oferta.id); }}>
                                            {processingOffer === oferta.id ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <Check className="h-4 w-4" />}
                                          </Button>
                                          <Button size="sm" variant="outline" disabled={!!processingOffer} onClick={(e) => { e.stopPropagation(); handleReject(oferta.id); }}>
                                            <X className="h-4 w-4" />
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Ofertas;